import styled, { createGlobalStyle } from 'styled-components';
import { theme } from './theme';

export const GlobalStyles = createGlobalStyle`
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&family=Playfair+Display:wght@400;700&display=swap');

  * {
    box-sizing: border-box;
    margin: 0;
    padding: 0;
  }

  body {
    font-family: ${theme.fonts.primary};
    color: ${theme.colors.text.primary};
    background: ${theme.colors.background};
    /* iOS Safari'de zoom/scroll sırasında kilitlenmeyi önlemek için
       ağır arka plan efektlerini varsayılan olarak kapat */
    background-attachment: scroll;
    background-size: cover;
    animation: none;
    line-height: 1.6;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    position: relative;
    overflow-x: hidden;
    
    /* 🚀 MOBİL SCROLL OPTİMİZASYONLARI */
    /* Mobilde smooth scrolling'i devre dışı bırak */
    -webkit-overflow-scrolling: touch;
    /* iOS Safari repaint optimizasyonu */
    -webkit-backface-visibility: hidden;
    backface-visibility: hidden;
    /* Mobil cihazlarda compositing layer oluşturmayı önle */
    will-change: auto;
    
    /* 🌟 SOFISTIKE ARKA PLAN EFEKTLERİ */
    /* iOS'ta compositing hatalarını azaltmak için pseudo arka planları
       varsayılan olarak devre dışı bırakıyoruz; masaüstünde etkinleştirilecek */
    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: ${theme.colors.gradient.luxury};
      opacity: 0.4;
      z-index: -2;
      animation: none;
      /* Mobil optimizasyon: GPU layer oluşturmayı önle */
      -webkit-backface-visibility: hidden;
      backface-visibility: hidden;
      will-change: auto;
    }
    
    &::after {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: 
        radial-gradient(circle at 20% 20%, rgba(99, 102, 241, 0.15) 0%, transparent 40%),
        radial-gradient(circle at 80% 80%, rgba(139, 92, 246, 0.12) 0%, transparent 40%),
        radial-gradient(circle at 50% 50%, rgba(245, 158, 11, 0.08) 0%, transparent 50%);
      z-index: -1;
      animation: none;
      /* Mobil optimizasyon: GPU layer oluşturmayı önle */
      -webkit-backface-visibility: hidden;
      backface-visibility: hidden;
      will-change: auto;
    }
    
    /* Mobil optimizasyonları */
    -webkit-text-size-adjust: 100%;
    -ms-text-size-adjust: 100%;
    -webkit-tap-highlight-color: transparent;
    touch-action: manipulation;
    
    /* iOS Safari için */
    -webkit-touch-callout: none;
    -webkit-user-select: none;
    -khtml-user-select: none;
    -moz-user-select: none;
    -ms-user-select: none;
    user-select: none;
    
    /* Metin seçimini sadece gerekli yerlerde aktif et */
    & p, & span, & div:not([role="button"]) {
      -webkit-user-select: text;
      -moz-user-select: text;
      -ms-user-select: text;
      user-select: text;
    }
  }
  
  /* 🎨 SOFISTIKE ANİMASYONLAR */
  @keyframes elegantShift {
    0% { background-position: 0% 50%; }
    50% { background-position: 100% 50%; }
    100% { background-position: 0% 50%; }
  }
  
  @keyframes luxuryPulse {
    0%, 100% { opacity: 0.4; transform: scale(1); }
    50% { opacity: 0.6; transform: scale(1.02); }
  }
  
  @keyframes subtleFloat {
    0% { transform: translateY(0) rotate(0deg); opacity: 0.8; }
    33% { transform: translateY(-10px) rotate(120deg); opacity: 1; }
    66% { transform: translateY(5px) rotate(240deg); opacity: 0.9; }
    100% { transform: translateY(0) rotate(360deg); opacity: 0.8; }
  }

  h1, h2, h3, h4, h5, h6 {
    font-family: ${theme.fonts.secondary};
    font-weight: 700;
    line-height: 1.2;
  }

  button {
    font-family: inherit;
    cursor: pointer;
    border: none;
    outline: none;
  }

  a {
    text-decoration: none;
    color: inherit;
  }

  img {
    max-width: 100%;
    height: auto;
  }

  ul, ol {
    list-style: none;
  }
  
  /* Mobil scroll iyileştirmeleri */
  html {
    /* iOS Safari'de double transition ve flicker'ı azaltmak için
       global smooth scroll'u kapatıyoruz. Desktop'ta açacağız. */
    scroll-behavior: auto;
    -webkit-overflow-scrolling: touch;
  }

  /* Masaüstünde arka plan efektlerini ve smooth scroll'u güvenle aç */
  @media (hover: hover) and (pointer: fine) and (min-width: 768px) {
    body {
      background-attachment: fixed;
      background-size: 200% 200%;
      animation: elegantShift 30s ease infinite;
      /* Masaüstünde will-change'i animasyon için ayarla */
      will-change: background-position;

      &::before {
        position: fixed;
        animation: luxuryPulse 20s ease-in-out infinite;
        will-change: opacity, transform;
      }

      &::after {
        position: fixed;
        animation: subtleFloat 40s linear infinite;
        will-change: transform, opacity;
      }
    }

    html { 
      scroll-behavior: smooth; 
    }
  }
  
  /* Mobil cihazlar için animasyonları tamamen kapat */
  @media (max-width: 767px) {
    body {
      /* Mobilde arka plan animasyonlarını kapat */
      animation: none !important;
      background-attachment: scroll !important;
      background-size: cover !important;
      
      &::before, &::after {
        animation: none !important;
        position: absolute !important;
        will-change: auto !important;
      }
    }
  }
  
  /* Mobile viewport fixes */
  @viewport {
    width: device-width;
    initial-scale: 1;
  }
  
  /* Touch-friendly button sizing */
  button, a, [role="button"] {
    min-height: 44px;
    min-width: 44px;
    touch-action: manipulation;
  }
  
  /* Prevent zoom on input focus (iOS Safari) */
  input, textarea, select {
    font-size: 16px;
  }
  
  /* Hide scrollbars but maintain functionality */
  ::-webkit-scrollbar {
    width: 0px;
    background: transparent;
  }
`;

export const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 ${theme.spacing.md};

  @media (min-width: ${theme.breakpoints.sm}) {
    padding: 0 ${theme.spacing.lg};
  }
`;

export const Card = styled.div`
  background: ${theme.colors.surface};
  border-radius: ${theme.borderRadius.lg};
  padding: ${theme.spacing.lg};
  box-shadow: ${theme.shadows.md};
  border: 1px solid ${theme.colors.border};
`;

export const Button = styled.button`
  padding: ${theme.spacing.sm} ${theme.spacing.lg};
  border-radius: ${theme.borderRadius.md};
  font-weight: 500;
  font-size: ${theme.fontSizes.md};
  transition: all 0.2s ease;
  display: inline-flex;
  align-items: center;
  gap: ${theme.spacing.sm};

  ${({ variant = 'primary' }) => {
    switch (variant) {
      case 'primary':
        return `
          background: ${theme.colors.primary};
          color: white;
          &:hover {
            background: #c0392b;
            transform: translateY(-1px);
          }
        `;
      case 'secondary':
        return `
          background: ${theme.colors.secondary};
          color: white;
          &:hover {
            background: #e67e22;
            transform: translateY(-1px);
          }
        `;
      case 'outline':
        return `
          background: transparent;
          color: ${theme.colors.primary};
          border: 2px solid ${theme.colors.primary};
          &:hover {
            background: ${theme.colors.primary};
            color: white;
          }
        `;
      default:
        return '';
    }
  }}
`;

export const Badge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: ${theme.spacing.xs} ${theme.spacing.sm};
  border-radius: ${theme.borderRadius.sm};
  font-size: ${theme.fontSizes.xs};
  font-weight: 500;
  gap: ${theme.spacing.xs};

  ${({ variant }) => {
    switch (variant) {
      case 'vegetarian':
        return `
          background: rgba(5, 150, 105, 0.1);
          color: ${theme.colors.quaternary};
          border: 1px solid rgba(5, 150, 105, 0.2);
        `;
      case 'vegan':
        return `
          background: rgba(5, 150, 105, 0.1);
          color: ${theme.colors.quaternary};
          border: 1px solid rgba(5, 150, 105, 0.2);
        `;
      case 'spicy':
        return `
          background: rgba(220, 38, 38, 0.1);
          color: ${theme.colors.error};
          border: 1px solid rgba(220, 38, 38, 0.2);
        `;
      default:
        return `
          background: ${theme.colors.surface};
          color: ${theme.colors.text.secondary};
          border: 1px solid ${theme.colors.border};
        `;
    }
  }}
`;
